import React, { useState, useEffect } from "react";
import { Skeleton, Table } from "antd";

export default function TableWithSkeleton({
  rowKey,
  rowSelection,
  columns,
  data,
  loading,
  length = 10,
  onChange,
  isCursor = false,
  onRowClick = () => { },
  showIndexColumn = true,
  hiddenColumns = [],
  page = 1,
  pageSize = 10,
  pagination = false,
  currentPage = 1,
  // New expandable props
  expandable = null,
  expandedRowRender = null,
  expandedRowColumns = null,
  expandRowByClick = false,
  defaultExpandAllRows = false,
  expandedRowKeys = null,
  onExpand = null,
  onExpandedRowsChange = null,
  rowExpandable = null,
  indentSize = 15,
  expandIcon = null,
  expandIconColumnIndex = 0,
}) {
  const [tableData, setTableData] = useState([]);
  const [internalExpandedKeys, setInternalExpandedKeys] = useState([]);

  let filterColumns = columns?.filter(
    (item) => !hiddenColumns?.includes(item?.title)
  );

  const numberColumn = {
    title: "No.",
    dataIndex: "no",
    render: (text, __, index) => {
      return loading ? (text) : (
        currentPage !== 1 ? <span className="ps-2"> {currentPage - 10 + index + 1} </span> : <span className="ps-2"> {(page - 1) * pageSize + index + 1} </span>
      );
    },
    width: 50,
  };

  const newColumns = !loading && showIndexColumn ? [numberColumn, ...filterColumns] : filterColumns;

  // When loading, override column renders to display skeleton (cell value) as-is.
  // Custom renders (e.g. Status badge, Actions) would otherwise treat Skeleton as data.
  const displayColumns = loading
    ? filterColumns.map((col) => ({ ...col, render: (value) => value }))
    : newColumns;

  const hiddenColumnsKey = hiddenColumns?.join(",") ?? "";

  useEffect(() => {
    const skeletonObj = {};
    filterColumns?.forEach((item) => {
      skeletonObj[item?.dataIndex] = (
        <Skeleton.Input className="table-skeleton" active={true} />
      );
    });
    const skeletonRows = Array.from({ length }, () => ({ ...skeletonObj }));
    setTableData(skeletonRows);
  }, [columns, hiddenColumnsKey, length, showIndexColumn]);

  const rowClassName = (record) => {
    return record?.isAdminCommission ? "admin-commission-row" : "";
  };

  // Create skeleton for expanded rows when loading
  const createExpandedRowSkeleton = () => {
    if (!expandedRowColumns || !loading) return null;

    const skeletonExpandedObj = {};
    expandedRowColumns.forEach((item) => {
      skeletonExpandedObj[item?.dataIndex] = (
        <Skeleton.Input className="table-skeleton" active={true} />
      );
    });

    return (
      <div className="expanded-row-skeleton">
        <Table
          columns={expandedRowColumns}
          dataSource={[skeletonExpandedObj, skeletonExpandedObj]}
          pagination={false}
          size="small"
          showHeader={false}
        />
      </div>
    );
  };

  // Handle expand state changes
  const handleExpand = (expanded, record) => {
    const key = record.key || record.userId || record.id;
    console.log(key)
    if (expanded) {
      setInternalExpandedKeys(prev => [...prev, key]);
    } else {
      setInternalExpandedKeys(prev => prev.filter(k => k !== key));
    }

    if (onExpand) {
      onExpand(expanded, record);
    }
  };

  const handleExpandedRowsChange = (keys) => {
    setInternalExpandedKeys(keys);
    if (onExpandedRowsChange) {
      onExpandedRowsChange(keys);
    }
  };

  useEffect(() => {
    if (!loading && data?.length) {
      const allKeys = data.map(
        (row) => row.key || row.userId || row.id
      );
      setInternalExpandedKeys(allKeys);
    }
  }, [loading, data]);


  // Handle expandable configuration
  const expandableConfig = expandable || (expandedRowRender && {
    expandedRowRender: loading
      ? () => createExpandedRowSkeleton()
      : expandedRowRender,
    expandRowByClick,
    defaultExpandAllRows,
    expandedRowKeys: loading
      ? []
      : (expandedRowKeys !== null
        ? expandedRowKeys
        : internalExpandedKeys),
    onExpand: handleExpand,
    onExpandedRowsChange: handleExpandedRowsChange,
    rowExpandable: rowExpandable || (() => true),
    indentSize,
    expandIcon,
    expandIconColumnIndex,
  });


  return (
    <Table
      className="table-responsive"
      scroll={{ x: true }}
      rowSelection={rowSelection}
      sortDirections={false}
      columns={displayColumns}
      dataSource={loading ? tableData : data}
      pagination={pagination}
      onChange={onChange}
      rowKey={(row, index) => {
        const key = row?.key ?? row?.id ?? (rowKey && row?.[rowKey]);
        return typeof key === "string" || typeof key === "number" ? key : index;
      }}
      rowClassName={rowClassName}
      expandable={expandableConfig}
      onRow={(record) => ({
        onClick: () => onRowClick(record),
        ...(isCursor && { style: { cursor: "pointer" } }),
      })}
    />
  );
}